import { Button, CommentInput } from "@dash/form";
import { BaseEntry, CommentEntry, EntriesList } from "@dash/history";
import { SectionHeader } from "@dash/page";
import { useProfiles } from "@megarax/features";
import { isToday, isYesterday } from "date-fns";
import React, { useState } from "react";
import { MdOutlineInsertDriveFile, MdOutlineMailOutline, MdOutlineSend, MdSwapHoriz, MdOutlineDownload } from "react-icons/md";
import { useQueryClient } from "react-query";

import { useClientManager } from "@megaron/react-clients";
import { MessageDto, TicketDetailDto, TicketHistoryEntry, ticketStatusLabels } from "@megaron/support-contracts";

import { toDisplayName } from "../shared/displayName";
import { getSupportTicketMessagesSectionTitle } from "./supportTicketMessagesSectionTitle";
import { SupportTicketEmailEntry } from "./SupportTicketEmailEntry";
import { SupportTicketPrintView } from "./SupportTicketPrintView";
import { SupportTicketReplyComposer } from "./SupportTicketReplyComposer";
import { useSupportTicketPdf } from "./useSupportTicketPdf";

type TicketComment = TicketDetailDto["notes"][number];
type TicketCommentMention = TicketComment["mentions"][number];

type Props = { ticket: TicketDetailDto };

const historyStatusLabels: Record<string, string> = {
  ...ticketStatusLabels,
  inProgress: ticketStatusLabels.open,
};

const formatDate = (date: Date): string => {
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  const timeStr = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  if (isToday(date)) return `Dziś o ${timeStr}`;
  if (isYesterday(date)) return `Wczoraj o ${timeStr}`;
  return `${date.toLocaleDateString("pl-PL")} ${timeStr}`;
};

const historyEntryTitle = (entry: TicketHistoryEntry): string => {
  if (entry.entryType === "ticketCreated") return "Utworzono zgłoszenie";
  const from = historyStatusLabels[entry.oldValue ?? ""] ?? entry.oldValue ?? "";
  const to = historyStatusLabels[entry.newValue ?? ""] ?? entry.newValue ?? "";
  return `Zmiana statusu: ${from} → ${to}`;
};

type MessageTimelineItem = { kind: "message"; data: MessageDto; date: Date };
type NoteTimelineItem = { kind: "note"; data: TicketComment; date: Date };
type HistoryTimelineItem = { kind: "history"; data: TicketHistoryEntry; date: Date };
type HistoryItem = NoteTimelineItem | HistoryTimelineItem;

const buildMessageTimeline = (ticket: TicketDetailDto): MessageTimelineItem[] =>
  ticket.messages
    .map((m) => ({ kind: "message" as const, data: m, date: new Date(m.createdAt) }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

const buildHistoryTimeline = (ticket: TicketDetailDto): HistoryItem[] => {
  const notes: HistoryItem[] = (ticket.notes ?? []).map((n) => ({
    kind: "note" as const,
    data: n,
    date: new Date(n.createdAt),
  }));
  const hasCreatedEntry = (ticket.history ?? []).some((h) => h.entryType === "ticketCreated");
  const syntheticCreated: HistoryItem[] = hasCreatedEntry
    ? []
    : [
        {
          kind: "history" as const,
          data: {
            id: ticket.id,
            entryType: "ticketCreated" as const,
            changedByEmail: null,
            oldValue: null,
            newValue: null,
            createdAt: ticket.createdAt,
          },
          date: new Date(ticket.createdAt),
        },
      ];
  const history: HistoryItem[] = (ticket.history ?? []).map((h) => ({
    kind: "history" as const,
    data: h,
    date: new Date(h.createdAt),
  }));
  return [...notes, ...history, ...syntheticCreated].sort((a, b) => b.date.getTime() - a.date.getTime());
};

export const SupportTicketMessagesAndNotes: React.FC<Props> = ({ ticket }) => {
  const queryClient = useQueryClient();
  const { profile } = useProfiles();
  const { fetchAndPrint, isLoading: isPdfLoading, printRef, attachmentsMap } = useSupportTicketPdf(ticket);
  const createCommentMutation = useClientManager("comments").createComment().useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
    },
  });
  const [notesInput, setNotesInput] = useState("");
  const [noteMentions, setNoteMentions] = useState<TicketCommentMention[]>([]);

  const sendNote = () => {
    const v = notesInput.trim();
    if (!v) return;
    createCommentMutation.mutate({
      resourceType: "supportTicket",
      resourceId: String(ticket.id),
      body: v,
      mentions: noteMentions,
    }, {
      onSuccess: () => {
        setNotesInput("");
        setNoteMentions([]);
      },
    });
  };

  const messageTimeline = buildMessageTimeline(ticket);
  const historyTimeline = buildHistoryTimeline(ticket);
  const messagesSectionTitle = getSupportTicketMessagesSectionTitle(ticket);

  const contactName = ticket.reporterName?.trim() || ticket.reporterEmail.split("@")[0];

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {messageTimeline.length > 0 && (
        <>
          <SupportTicketPrintView ref={printRef} ticket={ticket} attachmentsMap={attachmentsMap} />
          <SectionHeader
            isHr
            actions={
              <Button
                variant="outline"
                size="small"
                icon={<MdOutlineDownload />}
                onClick={() => { if (!isPdfLoading) void fetchAndPrint(); }}
                isLoading={isPdfLoading}
                isDisabled={isPdfLoading}
              >
                Pobierz PDF
              </Button>
            }
          >
            {messagesSectionTitle}
          </SectionHeader>
          <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {messageTimeline.map((item) => {
              const agentProfile = item.data.fromAgent ? profile(item.data.authorEmail ?? null) : null;
              const agentName = agentProfile
                ? `${agentProfile.firstName} ${agentProfile.lastName}`
                : item.data.authorEmail
                  ? toDisplayName(item.data.authorEmail.split("@")[0])
                  : "Agent";
              const fullName = item.data.fromAgent ? agentName : toDisplayName(contactName);
              return (
                <SupportTicketEmailEntry
                  key={item.data.id}
                  ticketId={ticket.id}
                  messageId={item.data.id}
                  fromName={fullName}
                  fromEmail={item.data.fromAgent ? (item.data.authorEmail ?? "") : ticket.reporterEmail}
                  time={formatDate(item.date)}
                  body={item.data.body}
                  isOutgoing={item.data.fromAgent}
                  attachments={item.data.attachments}
                />
              );
            })}
          </div>
        </>
      )}
      {ticket.threadId && <SupportTicketReplyComposer ticket={ticket} />}
      <SectionHeader isHr>Historia</SectionHeader>
      {historyTimeline.length > 0 && (
        <EntriesList>
          {historyTimeline.map((item) => {
            if (item.kind === "note") {
              const profileData = profile(item.data.authorEmail ?? null);
              const fullName = profileData
                ? `${profileData.firstName} ${profileData.lastName}`
                : toDisplayName(item.data.authorEmail?.split("@")[0] ?? "Notatka");
              return (
                <CommentEntry
                  key={item.data.id}
                  fullName={fullName}
                  time={formatDate(item.date)}
                  message={item.data.body}
                  imageSrc={profileData?.profilePictureUrl}
                />
              );
            }
            return (
              <BaseEntry
                key={item.data.id}
                title={historyEntryTitle(item.data)}
                icon={
                  item.data.entryType === "ticketCreated" ? (
                    <MdOutlineInsertDriveFile />
                  ) : item.data.entryType === "statusChanged" ? (
                    <MdSwapHoriz />
                  ) : (
                    <MdOutlineMailOutline />
                  )
                }
                time={formatDate(item.date)}
                additionalText={
                  item.data.changedByEmail ? toDisplayName(item.data.changedByEmail.split("@")[0]) : undefined
                }
              />
            );
          })}
        </EntriesList>
      )}
      <div css={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <CommentInput
          enableMentions={true}
          enableTags={false}
          commentValue={notesInput}
          mentionsValues={noteMentions}
          onChange={(comment, mentions) => {
            setNotesInput(comment);
            setNoteMentions(mentions ?? []);
          }}
          disableMaxLength
        />
        <div css={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            icon={<MdOutlineSend />}
            css={{ borderRadius: "50px", width: "auto", whiteSpace: "nowrap" }}
            onClick={sendNote}
            isDisabled={!notesInput.trim()}
            isLoading={"isLoading" in createCommentMutation ? createCommentMutation.isLoading : false}
          >
            Wyślij notatkę
          </Button>
        </div>
      </div>
    </div>
  );
};
